import type { GridAutoGeneratedGroupNode, GridDataGroupNode, GridLeafNode } from "@mui/x-data-grid-premium";
import { arraysHaveSameItems, distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import { separatedString } from "@entrilia/shared/utilities/stringHelper";
import type { Category, PortalRole, PortalRoleWithNoneValue } from "../../../../api/types/accessTypes";
import type {
  Contact,
  ContactDetails,
  ContactPermissions,
  FundPermissions,
  Investor,
  InvestorContact,
  InvestorContactPermissions,
} from "../../../../api/types/fundStructureTypes";

export interface ContactPermissionsChange {
  contact: Contact;
  original: CommunicationMatrixModel | undefined;
  modified: CommunicationMatrixModel;
}

export interface BulkPermissionsState {
  roles: PortalRole[] | undefined;
  categories: Record<string, boolean | undefined>;
  isPrimary: boolean | undefined;
  emailNotification: boolean | undefined;
}

export type CommunicationMatrixModel = ContactPermissions & InvestorContactPermissions & { investorId?: string };

interface InvestorName {
  id: string;
  name: string;
}

export type CommunicationMatrixEntityType = "Investor" | "Contact" | "Fund";

export const isEmptyFundInvestor = (model: CommunicationMatrixModel) => model.fundInvestorId === emptyGuid;

export const isEmptyInvestorContact = (model: CommunicationMatrixModel) => model.contactId === emptyGuid;

export const emptyGuid = "00000000-0000-0000-0000-000000000000";

export const getId = (investorName: string, fundInvestorId: string, contactId: string) =>
  `${investorName}-${fundInvestorId}-${contactId}`;

export const getModelId = (model: CommunicationMatrixModel) =>
  getId(model.investorName, model.fundInvestorId, model.contactId);

export const getGroupNodeModel = (
  models: CommunicationMatrixModel[],
  node: GridDataGroupNode | GridAutoGeneratedGroupNode | GridLeafNode
): CommunicationMatrixModel | undefined =>
  node.type === "group"
    ? models.find((model) => node.children.some((childId) => childId === getModelId(model)))
    : models.find((model) => getModelId(model) === node.id);

const includeExistingContactForSending = (contact: Contact) => !contact.invitationSentAt && Boolean(contact.email);

export const getContactChangesWhereInvitationWouldBeSent = (
  contacts: Contact[],
  originalPermissionModels: CommunicationMatrixModel[],
  modifiedPermissionModels: CommunicationMatrixModel[]
): ContactPermissionsChange[] => {
  const contactsMap = new Map(contacts.map((contact) => [contact.id, contact]));
  const toBeSent: ContactPermissionsChange[] = [];

  modifiedPermissionModels.forEach((model) => {
    const originalModel = originalPermissionModels.find(
      (originalModel) =>
        originalModel.fundInvestorId === model.fundInvestorId && originalModel.contactId === model.contactId
    );
    const contact = contactsMap.get(model.contactId);
    const isPortalRoleSet =
      model.roles.length > 0 &&
      (!originalModel || model.roles.some((role) => originalModel?.roles.indexOf(role) === -1));
    if (contact && includeExistingContactForSending(contact) && isPortalRoleSet) {
      toBeSent.push({ contact, original: originalModel, modified: model });
    }
  });
  return toBeSent;
};

export const createPermissionModelsForContacts = (investor: Investor, contacts: InvestorContact[]) => {
  const newModels: CommunicationMatrixModel[] = [];
  investor.funds.forEach((fundInvestor) => {
    contacts.forEach((contact) => {
      newModels.push({
        investorName: investor.title,
        investorId: investor.id,
        fundInvestorId: fundInvestor.fundInvestorId,
        fundId: fundInvestor.fundId,
        fundName: fundInvestor.fundName,
        contactId: contact.contactId,
        contactName: contact.name,
        contactEmail: contact.email,
        isPrimary: false,
        emailNotification: false,
        roles: [],
        categories: [],
      });
    });
  });
  return newModels;
};

export const createContactModelsForAddedInvestors = (
  investors: Investor[],
  contactDetails: ContactDetails
): CommunicationMatrixModel[] => {
  const newModels: CommunicationMatrixModel[] = [];
  const { contact, permissions } = contactDetails;
  investors.forEach((investor) => {
    const fundModels: CommunicationMatrixModel[] = [];
    investor.funds.forEach((fundInvestor) => {
      const existing = permissions.find((permission) => permission.fundInvestorId === fundInvestor.fundInvestorId);
      fundModels.push({
        investorName: investor.title,
        investorId: investor.id,
        fundInvestorId: fundInvestor.fundInvestorId,
        fundId: fundInvestor.fundId,
        fundName: fundInvestor.fundName,
        contactId: contact.id,
        contactName: contact.name,
        contactEmail: contact.email,
        isPrimary: existing?.isPrimary ?? false,
        emailNotification: existing?.emailNotification ?? false,
        roles: existing?.roles ?? [],
        categories: existing?.categories ?? [],
      });
    });

    newModels.push(...fundModels);
  });
  return newModels;
};

export const createFundModels = (permissions: FundPermissions[], fundId: string, fundName: string) => {
  const models: CommunicationMatrixModel[] = [];
  permissions.forEach((p) => {
    if (p.contacts.length > 0) {
      p.contacts.forEach((c) => {
        models.push({
          investorName: p.investorName,
          investorId: p.investorId,
          fundId,
          fundName,
          fundInvestorId: p.fundInvestorId,
          contactId: c.contactId,
          contactName: c.name,
          contactEmail: c.email,
          roles: c.roles,
          categories: c.categories,
          isPrimary: c.isPrimary,
          emailNotification: c.emailNotification,
        });
      });
    } else {
      models.push({
        investorName: p.investorName,
        investorId: p.investorId,
        fundInvestorId: p.fundInvestorId,
        fundId,
        fundName,
        contactId: emptyGuid,
        contactName: "",
        contactEmail: "",
        isPrimary: false,
        emailNotification: false,
        roles: [],
        categories: [],
      });
    }
  });
  return models;
};

export const createFundModelsForEmptyInvestorsContacts = (
  investors: InvestorName[],
  fundName: string
): CommunicationMatrixModel[] => {
  const newModels: CommunicationMatrixModel[] = [];
  investors.forEach((inv) => {
    //add empty to display in treedata as fund investor is not created yet at that moment (Investor => [FundInvestors] -> Contacts)
    newModels.push({
      investorId: inv.id,
      investorName: inv.name,
      fundInvestorId: emptyGuid,
      fundId: emptyGuid,
      fundName: fundName,
      contactId: emptyGuid,
      contactName: "",
      contactEmail: "",
      isPrimary: false,
      emailNotification: false,
      roles: [],
      categories: [],
    });
  });
  return newModels;
};

export const createFundPermissionModelsAddedContacts = (
  baseFundModel: CommunicationMatrixModel,
  contacts: InvestorContact[]
) =>
  contacts.map((contact) => ({
    ...baseFundModel,
    contactId: contact.contactId,
    contactName: contact.name,
    contactEmail: contact.email,
    isPrimary: false,
    emailNotification: false,
    roles: [],
    categories: [],
  }));

export const updateSelectedModelsWithPermissions = (
  models: CommunicationMatrixModel[],
  selectedIds: string[],
  permissionsState: BulkPermissionsState
) => {
  const selectedModels = models.filter((m) => selectedIds.includes(getModelId(m)));
  const updatedModels = selectedModels
    .filter((m) => m.contactEmail !== undefined)
    .map((m) => ({
      ...m,
      roles: permissionsState.roles !== undefined ? permissionsState.roles : m.roles,
      categories: updateCategoriesByPermissionState(m.categories, permissionsState.categories),
      isPrimary: permissionsState.isPrimary !== undefined ? permissionsState.isPrimary : m.isPrimary,
      emailNotification:
        permissionsState.emailNotification !== undefined ? permissionsState.emailNotification : m.emailNotification,
    }));
  const modelsWithoutUpdated = models.filter(
    (m) => !selectedIds.includes(getModelId(m)) || m.contactEmail === undefined
  );
  return [...modelsWithoutUpdated, ...updatedModels];
};

const updateCategoriesByPermissionState = (
  existing: string[],
  state: Record<string, boolean | undefined>
): string[] => {
  const categoryEntries = Object.entries(state);

  if (categoryEntries.length === 0) {
    return existing;
  }

  categoryEntries.forEach(([key, value]) => {
    if (value) {
      //check if category already exists
      if (!existing.includes(key)) {
        existing.push(key);
      }
    } else if (value === false) {
      //check if category exists
      if (existing.includes(key)) {
        existing.splice(existing.indexOf(key), 1);
      }
    }
  });
  return [...existing];
};

export const getBulkPermissionsState = (
  models: CommunicationMatrixModel[],
  categories: Category[]
): BulkPermissionsState => {
  return {
    roles: getIndeterminatePortalRolePermission(models),
    categories: getCategoriesIndeterminateMap(models, categories),
    emailNotification: getIndeterminateBooleanPermission(models, "emailNotification"),
    isPrimary: getIndeterminateBooleanPermission(models, "isPrimary"),
  };
};

export const getCategoriesIndeterminateMap = (
  models: CommunicationMatrixModel[],
  categories: Category[]
): Record<string, boolean | undefined> => {
  const categoriesIndeterminateMap: Record<string, boolean | undefined> = {};
  categories.forEach((c) => {
    const modelsWithCategory = models.filter((m) => m.categories.includes(c.externalId));
    const modelsWithoutCategory = models.filter((m) => !m.categories.includes(c.externalId));
    if (modelsWithCategory.length === models.length && modelsWithoutCategory.length === 0) {
      //all
      categoriesIndeterminateMap[c.externalId] = true;
    } else if (modelsWithCategory.length === 0 && modelsWithoutCategory.length === models.length) {
      //none
      categoriesIndeterminateMap[c.externalId] = false;
    } else {
      categoriesIndeterminateMap[c.externalId] = undefined;
    }
  });
  return categoriesIndeterminateMap;
};

export const getIndeterminateBooleanPermission = (
  models: CommunicationMatrixModel[],
  key: "emailNotification" | "isPrimary"
): boolean | undefined => {
  const distinctByKey = distinctBy(models, (m) => m[key]);
  if (distinctByKey.length === 1) {
    const [model] = distinctByKey;
    return model ? model[key] : undefined;
  }
  return undefined;
};

export const getIndeterminatePortalRolePermission = (models: CommunicationMatrixModel[]): PortalRole[] | undefined => {
  if (models.length === 0) {
    return undefined;
  }
  const [first, ...restRoles] = models.map((m) => m.roles);
  const allEqual = restRoles.every((roles) => first && arraysHaveSameItems(first, roles));
  return allEqual ? first : undefined;
};

// MUI executes query selector on the tree data path strings, so we need to ensure they are valid
const replaceNewLineWithSpace = (str: string) => str.replace(/\n/g, " ");

export const getFundMatrixTreeDataPath = (model: CommunicationMatrixModel) =>
  isEmptyInvestorContact(model)
    ? [model.investorName].map(replaceNewLineWithSpace)
    : [model.investorName, model.contactId].map(replaceNewLineWithSpace);

export const cloneFundModelWithoutChildren = (model: CommunicationMatrixModel) => {
  const result = { ...model };
  result.roles = [];
  result.categories = [];
  result.contactId = emptyGuid;
  result.contactName = "";
  result.contactEmail = "";
  return result;
};

export const investorGroupingKeySeparator = "╡";

export const getInvestorMatrixTreeDataPath = (model: CommunicationMatrixModel) =>
  [
    separatedString(investorGroupingKeySeparator)(model.contactName, model.contactId, model.contactEmail),
    model.fundName + model.fundInvestorId,
  ].map(replaceNewLineWithSpace);

export const cloneInvestorModelWithoutChildren = (model: CommunicationMatrixModel) => {
  const result = { ...model };
  result.roles = [];
  result.categories = [];
  result.fundInvestorId = emptyGuid;
  result.fundName = "";
  return result;
};

export const getContactMatrixTreeDataPath = (model: CommunicationMatrixModel) =>
  [model.investorName, model.fundName + model.fundInvestorId].map(replaceNewLineWithSpace);

export const cloneContactModelWithoutChildren = (model: CommunicationMatrixModel) => {
  const result = { ...model };
  result.roles = [];
  result.categories = [];
  result.fundInvestorId = emptyGuid;
  result.fundName = "";
  return result;
};

export const orderedPortalRoles: PortalRole[] = ["LP_Admin", "LP_Member"];

export const resolvePortalRoles = (roles: PortalRole[] | undefined): PortalRoleWithNoneValue =>
  roles === undefined ? "None" : (orderedPortalRoles.find((r) => roles.includes(r)) ?? "None");

export const portalRowToArray = (role: PortalRoleWithNoneValue): PortalRole[] => (role === "None" ? [] : [role]);
